// ============================================================================
// THE CYCLE — Pitch Sequencing Analysis Card
// ============================================================================
// Shows transition matrix, count-based pitch selection, and first-pitch tendencies.

import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, CircularProgress, Chip, useTheme, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip,
} from '@mui/material';
import { pitchSplitsApi } from '../services/apiService';

// Pitch type colors
import { getPitchName as pitchLabel, PITCH_COLORS } from '../utils/pitchTypes';

const PitchSequencingCard = ({ team, playerName, season, perspective = 'pitcher', gameType }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!team || !playerName) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await pitchSplitsApi.getSequencing(perspective, team, playerName, season, { gameType });
        setData(res);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [team, playerName, season, perspective, gameType]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Typography color="error" variant="body2">{error}</Typography>;
  if (!data) return null;

  const { transitions, countPatterns, firstPitch, totalAtBats, totalPitches } = data;
  const fromTypes = transitions ? Object.keys(transitions) : [];
  const allToTypes = new Set();
  fromTypes.forEach(f => {
    if (transitions[f]) Object.keys(transitions[f]).forEach(t => allToTypes.add(t));
  });
  const toTypes = [...allToTypes];

  // Count pattern — exclude terminal counts (walk = 4 balls, K = 3 strikes already happened)
  const countEntries = countPatterns ? Object.entries(countPatterns)
    .filter(([count]) => {
      const [b, s] = count.split('-').map(Number);
      return b < 4 && s < 3; // only counts where a pitch can still be thrown
    })
    .sort((a, b) => {
      const [ba, sa] = a[0].split('-').map(Number);
      const [bb, sb] = b[0].split('-').map(Number);
      return (ba * 10 + sa) - (bb * 10 + sb);
    }) : [];

  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={800}>Pitch Sequencing</Typography>
        <Chip label={`${totalAtBats || 0} AB`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
        <Chip label={`${totalPitches || 0} pitches`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
      </Box>

      {/* Transition Matrix */}
      {fromTypes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Pitch→Next Pitch Transition Matrix
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Rows = current pitch. Columns = next pitch thrown. Values = percentage.
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: '0.75rem' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70 }}>From →</TableCell>
                  {toTypes.map(t => (
                    <Tooltip key={t} title={pitchLabel(t)} arrow placement="top">
                      <TableCell align="center" sx={{ fontWeight: 700, color: PITCH_COLORS[t] || 'inherit', cursor: 'default' }}>
                        {pitchLabel(t)}
                      </TableCell>
                    </Tooltip>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fromTypes.map(from => (
                  <TableRow key={from} sx={{ '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }}>
                    <Tooltip title={pitchLabel(from)} arrow placement="left">
                      <TableCell sx={{ fontWeight: 700, color: PITCH_COLORS[from] || 'inherit', cursor: 'default' }}>{pitchLabel(from)}</TableCell>
                    </Tooltip>
                    {toTypes.map(to => {
                      const cell = transitions[from]?.[to];
                      const pct = cell?.pct || 0;
                      const count = cell?.count || 0;
                      return (
                        <Tooltip key={to} title={`${pitchLabel(from)} → ${pitchLabel(to)}: ${count}x (${pct}%)`} arrow placement="top">
                          <TableCell
                            align="center"
                            sx={{
                              bgcolor: pct > 0 ? alpha(PITCH_COLORS[to] || theme.palette.primary.main, Math.min(pct / 50, 0.5)) : 'transparent',
                              fontWeight: pct >= 25 ? 700 : 400,
                              color: pct >= 35 ? '#fff' : 'inherit',
                              borderRadius: 0.5,
                            }}
                          >
                            {pct > 0 ? `${pct}` : '—'}
                          </TableCell>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* First Pitch Tendencies */}
      {firstPitch && Object.keys(firstPitch).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>First Pitch Tendency</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(firstPitch)
              .sort((a, b) => b[1].pct - a[1].pct)
              .map(([pt, info]) => (
                <Box key={pt} sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  minWidth: 60, p: 1, borderRadius: 1.5,
                  border: `1px solid ${alpha(PITCH_COLORS[pt] || theme.palette.divider, 0.4)}`,
                  bgcolor: alpha(PITCH_COLORS[pt] || theme.palette.divider, 0.06),
                }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: PITCH_COLORS[pt] || 'inherit' }}>
                    {pitchLabel(pt)}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1rem' }}>
                    {info.pct}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {info.count} AB
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {/* Count Patterns */}
      {countEntries.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Pitch Selection by Count
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Shows the most-used pitch for each count state. Hover for full breakdown. Terminal counts (walk/strikeout) excluded.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {countEntries.map(([count, pitches]) => {
              const sorted = Object.entries(pitches).sort((a, b) => b[1].pct - a[1].pct);
              const topPitch = sorted[0];
              const [b, s] = count.split('-').map(Number);
              const countLabel = b === 3 && s === 2 ? `${count} (Full)` : count;
              return (
                <Tooltip
                  key={count}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Count {countLabel}</Typography>
                      {sorted.map(([pt, d]) => (
                        <Typography key={pt} variant="caption" sx={{ display: 'block', fontSize: '0.72rem' }}>
                          {pitchLabel(pt)}: {d.pct}% ({d.count})
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                >
                  <Box sx={{
                    p: 0.75, borderRadius: 1, minWidth: 62, textAlign: 'center',
                    border: `1px solid ${alpha(PITCH_COLORS[topPitch?.[0]] || theme.palette.divider, 0.35)}`,
                    bgcolor: alpha(PITCH_COLORS[topPitch?.[0]] || theme.palette.divider, 0.07),
                    cursor: 'help',
                  }}>
                    <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.8rem', display: 'block' }}>{countLabel}</Typography>
                    <Typography variant="caption" display="block" sx={{
                      fontSize: '0.7rem', color: PITCH_COLORS[topPitch?.[0]] || 'text.secondary', fontWeight: 700, lineHeight: 1.3,
                    }}>
                      {pitchLabel(topPitch?.[0])}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.75 }}>
                      {topPitch?.[1]?.pct}%
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default PitchSequencingCard;
